import { useState, useEffect, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import type { Artist } from '../types';

interface ArtistPagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

export const useArtists = (propArtists?: Artist[], artist_pagination?: ArtistPagination) => {
  const [artistOptions, setArtistOptions] = useState<Artist[]>([]);
  const [isLoadingArtists, setIsLoadingArtists] = useState(false);
  const [artistOptionsLoaded, setArtistOptionsLoaded] = useState(false);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistPage, setArtistPage] = useState(1);
  const [hasMoreArtists, setHasMoreArtists] = useState(false); // Changed to false since we load all
  const [debouncedSearch] = useDebouncedValue(artistSearch, 300);

  // Initialize with prop artists but always load all artists
  useEffect(() => {
    // Set initial artists from props if available
    if (propArtists && propArtists.length > 0) {
      setArtistOptions(propArtists);
    }

    // Always fetch all artists to ensure complete coverage
    fetchAllArtists();
  }, [propArtists]);

  // Load more artists (kept for backward compatibility, but not used)
  const loadMoreArtists = async () => {
    console.log('loadMoreArtists called, but all artists already loaded');
    return; // No-op since we load all artists initially
  };

  // Fetch all artists
  const fetchAllArtists = async () => {
    if (artistOptionsLoaded) {
      console.log('All artists already loaded, skipping fetch');
      return; // Already loaded all artists
    }

    setIsLoadingArtists(true);
    try {
      console.log('🎨 Fetching all artists...');
      const res = await fetch('/artists/list?all=true', {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();

      console.log(`🎨 Loaded ${data.artists?.length || 0} artists`);
      setArtistOptions(data.artists || []);
      setArtistOptionsLoaded(true);
      setHasMoreArtists(false); // We have all artists now
    } catch (err) {
      console.error('Failed to load all artists:', err);
    } finally {
      setIsLoadingArtists(false);
    }
  };

  // Search artists (client-side since we have all artists)
  const searchArtists = async (query: string) => {
    if (!query.trim()) {
      return mergedArtists;
    }

    // Since we have all artists loaded, do client-side filtering
    return mergedArtists.filter((artist) => artist.name.toLowerCase().includes(query.toLowerCase()));
  };

  // Merge prop artists with fetched artists
  const mergedArtists = useMemo(() => {
    const allArtists = [...artistOptions];

    // Add any artists from propArtists that aren't already in artistOptions
    if (propArtists && Array.isArray(propArtists)) {
      propArtists.forEach((propArtist) => {
        const exists = allArtists.find((artist) => artist.id === propArtist.id);
        if (!exists) {
          allArtists.unshift(propArtist); // Add to beginning so selected artists appear first
        }
      });
    }

    return allArtists;
  }, [artistOptions, propArtists]);

  return {
    artistOptions,
    isLoadingArtists,
    artistOptionsLoaded,
    artistSearch,
    setArtistSearch,
    artistPage,
    setArtistPage,
    hasMoreArtists,
    setHasMoreArtists,
    debouncedSearch,
    fetchAllArtists,
    loadMoreArtists,
    searchArtists,
    mergedArtists,
    // Pagination info (legacy support)
    totalPages: 1, // Since we load all artists, there's only 1 page
    totalCount: mergedArtists.length,
  };
};
